import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { dataRoomService } from '@/services/dataRoomService';
import { Project, ProjectUnlockData, ProjectGraphData } from '@/types/project';
import { Button } from '@/components/ui/button';
import { PageTemplate } from '@/components/templates';
import { CONTAINER_SIZES } from '@/constants';

import { 
  FileText,
  Unlock, 
  BarChart3,
  ChevronLeft
} from 'lucide-react';
import { ProjectRFQModal } from '@/components/organisms/modals';

// Tab components
import ProjectOverview from './components/ProjectOverview';
import ProjectUnlocks from './components/ProjectUnlocks';
import ProjectAnalytics from './components/ProjectAnalytics';

interface Tab {
  id: string;
  label: string;
  icon: React.ReactNode;
}

const tabs: Tab[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: <FileText size={12} />
  },
  {
    id: 'unlocks',
    label: 'Unlocks',
    icon: <Unlock size={12} />
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: <BarChart3 size={12} />
  }
];

const ProjectDetailScreen: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const navigate = useNavigate();  const [activeTab, setActiveTab] = useState('overview');
  const [project, setProject] = useState<Project | null>(null);
  const [unlockData, setUnlockData] = useState<ProjectUnlockData | null>(null);
  const [graphData, setGraphData] = useState<ProjectGraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showRFQModal, setShowRFQModal] = useState(false);

  // Set active tab based on URL path
  useEffect(() => {
    if (location.pathname.includes('/unlock')) {
      setActiveTab('unlocks');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!projectId) {
      setError('Project ID is required');
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchProjectData = async () => {
      console.log('fetchProjectData started for projectId:', projectId);
      try {
        setLoading(true);
        setError(null);

        // Fetch project data with individual error handling
        let projectDetail, unlockResponse, graphResponse;
        
        try {
          projectDetail = await dataRoomService.getProjectDetail(projectId, true);
          if (isMounted) {
            setProject(projectDetail);
          }
        } catch (error) {
          console.error('Failed to fetch project details:', error);
          if (isMounted) {
            setError('Failed to load project details');
          }
          // Don't set loading here - let finally block handle it
          return;
        }
        
        // Only fetch unlock and graph data if project detail was successful
        if (!isMounted) return;
        
        try {
          unlockResponse = await dataRoomService.getProjectUnlocks(projectId);
          // getProjectUnlocks returns null for 404 errors, so this is safe
          if (isMounted) {
            if (unlockResponse) {
              setUnlockData(unlockResponse.unlock);
            } else {
              setUnlockData(null);
            }
          }
        } catch (error) {
          // This should rarely happen since 404s are handled in the service
          console.warn('Failed to fetch unlock data:', error);
          if (isMounted) {
            setUnlockData(null);
          }
        }
        
        if (!isMounted) {
          return;
        }
        
        try {
          graphResponse = await dataRoomService.getProjectGraph(projectId);
          
          if (isMounted) {
            // Ensure we have valid graph data before setting it
            if (graphResponse && typeof graphResponse === 'object') {
              setGraphData(graphResponse);
            } else {
              setGraphData(null);
            }
          }
        } catch (error) {
          // Don't fail the entire page if graph data is missing
          if (isMounted) {
            setGraphData(null);
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch project data:', err);
        if (isMounted) {
          setError(err.message || 'Failed to load project data');
        }
      } finally {
        // Always set loading to false, regardless of mount state
        setLoading(false);
      }
    };

    fetchProjectData();
    
    return () => {
      isMounted = false;
    };
  }, [projectId]);

  // Switch to overview tab if user is on unlocks tab but unlock data is not available
  useEffect(() => {
    if (activeTab === 'unlocks' && !unlockData && !loading) {
      setActiveTab('overview');
    }
  }, [activeTab, unlockData, loading]);

  const handlePlaceInquiry = () => {
    if (project) {
      setSelectedProject(project);
      setShowRFQModal(true);
    }
  };

  const handleRFQSubmitted = () => {
    console.log('RFQ submitted successfully');
    setShowRFQModal(false);
    setSelectedProject(null);
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }

    if (!project) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Project not found</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'overview':
        return <ProjectOverview project={project} loading={false} onPlaceInquiry={handlePlaceInquiry} />;
      case 'unlocks':
        return unlockData ? <ProjectUnlocks unlockData={unlockData} /> : (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">No unlock data available for this project</p>
          </div>
        );
      case 'analytics':
        // Always pass graphData to ProjectAnalytics - it will handle null/undefined/empty data
        return <ProjectAnalytics graphData={graphData} projectId={projectId || ''} project={project} loading={false} />;
      default:
        return <ProjectOverview project={project} loading={false} onPlaceInquiry={handlePlaceInquiry} />;
    }
  };

  return (
    <PageTemplate maxWidth={CONTAINER_SIZES.WIDE}>
      <div className="space-y-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)] hover:text-[#2d2923] dark:hover:text-[#fdfdfd] transition-colors mb-2"
        >
          <ChevronLeft size={16} />
          <span className="font-['Plus_Jakarta_Sans'] font-medium text-[14px]">Back</span>
        </button>
        {/* Project Header Section */}
        <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(28,28,28,0.32)] border border-white dark:border-[rgba(255,255,255,0.1)] overflow-hidden">
          <div className="px-7 pt-6 pb-0">
            {/* Top Row: Logo, Name, Description + Buttons */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1 max-w-[894px]">
                {/* Logo and Name */}
                <div className="flex items-center gap-[10px] mb-[6px]">
                  {project?.logo ? (
                    <div className="w-7 h-7 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                      <img
                        src={project.logo}
                        alt={project.project_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#c8ed03] flex-shrink-0" />
                  )}
                  <h1 className="font-['Plus_Jakarta_Sans'] font-semibold text-[24px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd]">
                    {loading ? 'Loading...' : project?.project_name || 'Project'}
                  </h1>
                </div>
                {/* Description */}
                {project?.description && (
                  <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] leading-[24px] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                    {project.description}
                  </p>
                )}
              </div>

              {/* Buttons */}
              {!loading && project && (
                <div className="flex items-center gap-[6px] flex-shrink-0 ml-4">
                  <Button
                    onClick={handlePlaceInquiry}
                    className="bg-[#c8ed03] hover:bg-[#b5d603] text-[#2d2923] font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] px-[18px] py-[10px] h-auto rounded-none"
                  >
                    Place Inquiry
                  </Button>
                  {project.link && (
                    <Button
                      variant="outline"
                      className="bg-white dark:bg-transparent border-[rgba(45,41,35,0.16)] dark:border-[rgba(253,253,253,0.16)] text-[#2d2923] dark:text-[#fdfdfd] font-['Plus_Jakarta_Sans'] font-medium text-[13px] leading-[18px] px-[18px] py-[10px] h-auto rounded-none"
                      asChild
                    >
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Visit Website
                      </a>
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="flex items-end">
              {tabs
                .filter(tab => {
                  // Hide Unlocks tab if unlock data is not available
                  if (tab.id === 'unlocks' && !unlockData) {
                    return false;
                  }
                  return true;
                })
                .map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-[4px] px-4 py-[12px] relative
                      font-['Plus_Jakarta_Sans'] font-medium text-[12px] leading-[16px]
                      transition-colors
                      ${activeTab === tab.id 
                        ? 'text-[#2d2923] dark:text-[#fdfdfd]' 
                        : 'text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)] hover:text-[rgba(45,41,35,0.72)] dark:hover:text-[rgba(253,253,253,0.72)]'
                      }
                    `}
                  >
                    <span className={activeTab === tab.id ? 'text-[#2d2923] dark:text-[#fdfdfd]' : 'text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]'}>
                      {tab.icon}
                    </span>
                    <span>{tab.label}</span>
                    {/* Active indicator line */}
                    {activeTab === tab.id && (
                      <div className="absolute bottom-0 left-1 right-1 h-[3px] bg-[#c8ed03] rounded-t-[2px]" />
                    )}
                  </button>
                ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="min-h-[600px]"
        >
          {renderTabContent()}
        </motion.div>
      </div>

      {/* RFQ Modal */}
      <ProjectRFQModal
        isOpen={showRFQModal}
        onClose={() => {
          setShowRFQModal(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
        onRFQSubmitted={handleRFQSubmitted}
      />
    </PageTemplate>
  );
};

export default ProjectDetailScreen; 